import React from 'react';
import { useQuote } from '../context/QuoteContext';
import { getRefinedAnalysis } from '../services/openai';

interface ClarifyingQuestionsProps {
  onComplete: () => void;
  onPriceUpdate: (priceRange: { min: number; max: number; confidence: 'low' | 'medium' | 'high' }) => void;
}

const ClarifyingQuestions: React.FC<ClarifyingQuestionsProps> = ({ onComplete, onPriceUpdate }) => {
  const { state, dispatch } = useQuote();
  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(0);
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleAnswer = async (answer: string | boolean) => {
    const currentQuestion = state.clarifyingQuestions[currentQuestionIndex];
    
    dispatch({
      type: 'UPDATE_QUESTION_ANSWER',
      payload: { id: currentQuestion.id, answer }
    });

    if (currentQuestionIndex < state.clarifyingQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setIsProcessing(true);
      try {
        const answers = state.clarifyingQuestions.reduce((acc, q) => ({
          ...acc,
          [q.id]: q.answer
        }), {});

        const refinedAnalysis = await getRefinedAnalysis(
          state.workDescription?.description || '',
          state.workDescription?.category || '',
          answers
        );

        dispatch({
          type: 'SET_PRICE_RANGE',
          payload: {
            ...refinedAnalysis.priceRange,
            confidence: refinedAnalysis.confidence
          }
        });

        onPriceUpdate({
          min: refinedAnalysis.priceRange.min,
          max: refinedAnalysis.priceRange.max,
          confidence: refinedAnalysis.confidence
        });

      } catch (error) {
        console.error('Error getting refined analysis:', error);
      } finally {
        setIsProcessing(false);
        onComplete();
      }
    }
  };

  if (!state.clarifyingQuestions.length) {
    return null;
  }

  const currentQuestion = state.clarifyingQuestions[currentQuestionIndex];

  return (
    <div className="animate-fadeIn">
      <h3 className="text-lg font-medium mb-4">
        Help us provide a more accurate estimate
      </h3>
      
      <div className="mb-6">
        <p className="text-gray-600 mb-4">{currentQuestion.question}</p>
        
        {currentQuestion.type === 'boolean' && (
          <div className="flex gap-3">
            <button
              onClick={() => handleAnswer(true)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:border-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </div>
              ) : (
                'Yes'
              )}
            </button>
            <button
              onClick={() => handleAnswer(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:border-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </div>
              ) : (
                'No'
              )}
            </button>
          </div>
        )}
        
        {currentQuestion.type === 'select' && currentQuestion.options && (
          <div className="space-y-2">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                className="w-full px-4 py-2 text-left border border-gray-300 rounded-lg hover:border-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  option
                )}
              </button>
            ))}
          </div>
        )}
        
        {currentQuestion.type === 'text' && (
          <div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder="Type your answer"
              disabled={isProcessing}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAnswer((e.target as HTMLInputElement).value);
                }
              }}
            />
            <button
              onClick={() => {
                const input = document.querySelector('input') as HTMLInputElement;
                handleAnswer(input.value);
              }}
              className="mt-2 w-full bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </div>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        )}
      </div>
      
      <div className="flex justify-between items-center text-sm text-gray-500">
        <span>Question {currentQuestionIndex + 1} of {state.clarifyingQuestions.length}</span>
        <div className="flex gap-1">
          {state.clarifyingQuestions.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full ${
                index === currentQuestionIndex ? 'bg-black' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClarifyingQuestions;